import { nanoid } from '../utils/nanoid.js'
import type { ChatMessage } from './types.js'

// Use proxy server URL - can be configured via environment
const PROXY_URL = import.meta.env.VITE_PROXY_URL || 'http://localhost:3103'

const SYSTEM_PROMPT = `You are an AI assistant integrated into WireframeLM, a tool for building wireframes and mockups using standard HTML + Tailwind CSS with a minimal set of semantic Web Components.

## WireframeLM Philosophy

WireframeLM uses **standard HTML + Tailwind CSS** for layout and structure. Only use custom Web Components when they provide real value beyond standard HTML elements.

## Available Web Components

You have access to these semantic components:

### wf-icon
Material Symbols icon with automatic font handling and variable font features.
Attributes:
- \`name\` (required): Icon name from Material Symbols (e.g., "home", "search", "menu")
- \`size\`: "sm" | "md" | "lg" | "xl" (default: "md")
- \`fill\`: "0" | "1" (default: "0", use "1" for filled icons)
- \`weight\`: "100" | "200" | "300" | "400" | "500" | "600" | "700" (default: "400")

Example:
\`\`\`html
<wf-icon name="home" size="lg" fill="1"></wf-icon>
\`\`\`

### wf-avatar
User avatar with automatic initials generation from labels.
Attributes:
- \`src\`: Image URL (optional)
- \`label\`: User's name for generating initials (required if no src)
- \`size\`: "sm" | "md" | "lg" | "xl" (default: "md")
- \`variant\`: "circle" | "rounded" | "square" (default: "circle")

Example:
\`\`\`html
<wf-avatar label="John Doe" size="lg" variant="circle"></wf-avatar>
<wf-avatar src="/avatar.jpg" label="Jane Smith"></wf-avatar>
\`\`\`

### wf-badge
Status badge with semantic variant styling.
Attributes:
- \`label\`: Badge text (required)
- \`variant\`: "neutral" | "primary" | "success" | "warning" | "danger" (default: "neutral")
- \`size\`: "sm" | "md" | "lg" (default: "md")

Example:
\`\`\`html
<wf-badge label="Active" variant="success"></wf-badge>
<wf-badge label="Pending" variant="warning" size="sm"></wf-badge>
\`\`\`

### wf-divider
Visual separator with orientation support.
Attributes:
- \`variant\`: "solid" | "dashed" (default: "solid")
- \`orientation\`: "horizontal" | "vertical" (default: "horizontal")

Example:
\`\`\`html
<wf-divider variant="solid"></wf-divider>
<wf-divider variant="dashed" orientation="vertical"></wf-divider>
\`\`\`

## Layout: Use Standard HTML + Tailwind

For layout and structure, always use standard HTML with Tailwind classes:

**Flexbox:**
\`\`\`html
<div class="flex flex-col gap-4">
  <div>Item 1</div>
  <div>Item 2</div>
</div>
\`\`\`

**Grid:**
\`\`\`html
<div class="grid grid-cols-3 gap-4">
  <div>Column 1</div>
  <div>Column 2</div>
  <div>Column 3</div>
</div>
\`\`\`

**Spacing:**
\`\`\`html
<div class="h-6"></div> <!-- Spacer -->
<div class="p-6">Content</div> <!-- Padding -->
<div class="mb-4">Content</div> <!-- Margin -->
\`\`\`

## Semantic Color Token System

WireframeLM uses a semantic color token system based on CSS variables for theme-agnostic designs:

**Background Colors:**
- \`bg-background\`: Main background
- \`bg-muted\`: Muted background (sidebars, secondary areas)
- \`bg-card\`: Card/panel background
- \`bg-primary\`: Primary action background
- \`bg-secondary\`: Secondary action background
- \`bg-accent\`: Accent elements

**Text Colors:**
- \`text-foreground\`: Main text
- \`text-muted-foreground\`: Muted text (labels, secondary text)
- \`text-card-foreground\`: Text on cards
- \`text-primary-foreground\`: Text on primary backgrounds
- \`text-secondary-foreground\`: Text on secondary backgrounds
- \`text-accent-foreground\`: Text on accent backgrounds

**Borders:**
- \`border-border\`: Standard borders

**Example wireframe structure:**
\`\`\`html
<div class="flex min-h-screen bg-background text-foreground">
  <div class="flex gap-6 p-6">
    <!-- Sidebar -->
    <div class="flex flex-col gap-4 w-64 bg-muted p-6 rounded-lg border border-border">
      <div class="flex gap-2 items-center">
        <wf-icon name="menu" size="lg" />
        <h2 class="text-xl font-semibold">Brand</h2>
      </div>
    </div>

    <!-- Main Content -->
    <div class="flex flex-col gap-6 flex-1">
      <h1 class="text-3xl font-bold">Dashboard</h1>
      <!-- Content -->
    </div>
  </div>
</div>
\`\`\`

## Your Role

You help users create and modify wireframes by:
1. Understanding their requirements and design intent
2. Generating clean, semantic HTML + Tailwind markup
3. Using Web Components only where they add value (icons, avatars, badges, dividers)
4. Following the semantic color token system for theme compatibility
5. Making incremental, focused changes using tool calls

## Tool Usage

You have two tools for modifying wireframes:

### 1. update_html - For complete designs or major changes
Use when:
- Creating a new wireframe from scratch
- Making major structural changes
- User asks to "start over" or "create a new design"
- Replacing large sections of the layout

### 2. replace_text - For surgical edits
Use when:
- Making small, focused changes to existing markup
- Adding a single element
- Changing specific text or attributes
- Modifying a section without touching the rest

**Best practices:**
- Prefer \`replace_text\` for incremental changes (it's faster and preserves more context)
- Use \`update_html\` for complete rewrites or major restructuring
- Always preserve existing structure unless explicitly asked to change it
- Use semantic color tokens (not arbitrary Tailwind color classes like \`bg-blue-500\`)
- Keep markup clean and well-formatted
- Explain what changes you're making

Be helpful, concise, and focused on creating high-quality wireframes.`

export interface AIServiceConfig {
  onToolCall?: (toolName: string, toolInput: unknown) => void
  onChunk?: (text: string) => void
  onComplete?: (fullText: string) => void
  onError?: (error: Error) => void
}

export interface SendMessageOptions {
  userMessage: string
  chatHistory: ChatMessage[]
  currentMarkup: string
  imageAttachment?: {
    dataUrl: string
    width: number
    height: number
  }
  selectedNodeHtml?: string | null
}

interface MessageParam {
  role: 'user' | 'assistant'
  content: string | ContentBlock[]
}

interface ContentBlock {
  type: 'text' | 'tool_use' | 'image'
  text?: string
  id?: string
  name?: string
  input?: unknown
  source?: {
    type: 'base64'
    media_type: string
    data: string
  }
}

interface Tool {
  name: string
  description: string
  input_schema: {
    type: string
    properties: Record<string, unknown>
    required: string[]
  }
}

export class AIService {
  private config: AIServiceConfig

  constructor(config: AIServiceConfig) {
    this.config = config
  }

  async sendMessage(options: SendMessageOptions): Promise<ChatMessage> {
    const { userMessage, chatHistory, currentMarkup, imageAttachment, selectedNodeHtml } = options

    // Build messages array
    const messages: MessageParam[] = []

    // Add chat history (without tool calls, since we don't track tool results)
    // The current markup is sent with each message, so the AI has full context
    for (const msg of chatHistory) {
      if (msg.role === 'user') {
        // For user messages with images, include the image in history
        if (msg.imageAttachment) {
          // Extract media type from data URL
          const mediaTypeMatch = msg.imageAttachment.dataUrl.match(/^data:(image\/[^;]+);base64,/)
          const mediaType = mediaTypeMatch ? mediaTypeMatch[1] : 'image/png'

          const content: ContentBlock[] = [
            {
              type: 'image',
              source: {
                type: 'base64',
                media_type: mediaType as string,
                data: msg.imageAttachment.dataUrl.split(',')[1] || ''
              }
            },
            {
              type: 'text',
              text: msg.content
            }
          ]
          messages.push({
            role: 'user',
            content
          })
        } else {
          messages.push({
            role: 'user',
            content: msg.content
          })
        }
      } else if (msg.role === 'assistant') {
        // Only include the text content if it's non-empty
        // Skip assistant messages with empty content (tool-only messages)
        if (msg.content && msg.content.trim()) {
          messages.push({
            role: 'assistant',
            content: msg.content
          })
        }
      }
    }

    // Add current message with context
    let currentMessageText = `Current HTML markup:
\`\`\`html
${currentMarkup}
\`\`\`
`

    if (selectedNodeHtml) {
      currentMessageText += `
Selected element (user clicked on this):
\`\`\`html
${selectedNodeHtml}
\`\`\`

`
    }

    currentMessageText += userMessage

    console.log('📤 Sending to AI with markup length:', currentMarkup.length)
    console.log('📤 Markup preview (first 500 chars):', currentMarkup.substring(0, 500))

    // If there's an image attachment, build a multi-part content block
    if (imageAttachment) {
      // Extract media type from data URL
      const mediaTypeMatch = imageAttachment.dataUrl.match(/^data:(image\/[^;]+);base64,/)
      const mediaType = mediaTypeMatch ? mediaTypeMatch[1] : 'image/png'

      const content: ContentBlock[] = [
        {
          type: 'image',
          source: {
            type: 'base64',
            media_type: mediaType as string,
            data: imageAttachment.dataUrl.split(',')[1] || ''
          }
        },
        {
          type: 'text',
          text: currentMessageText
        }
      ]
      messages.push({
        role: 'user',
        content
      })
    } else {
      messages.push({
        role: 'user',
        content: currentMessageText
      })
    }

    // Define tools
    const tools: Tool[] = [
      {
        name: 'update_html',
        description: 'Replace the entire HTML markup. Use for new designs from scratch or major structural changes. Prefer replace_text for small, focused edits.',
        input_schema: {
          type: 'object',
          properties: {
            html: {
              type: 'string',
              description: 'The complete updated HTML markup'
            },
            summary: {
              type: 'string',
              description: 'A brief summary of what changes were made'
            }
          },
          required: ['html', 'summary']
        }
      },
      {
        name: 'replace_text',
        description: 'Make surgical edits by replacing specific text in the HTML. Use for small, focused changes like adding an element, modifying attributes, or changing specific content. This is faster and preserves more context than update_html.',
        input_schema: {
          type: 'object',
          properties: {
            old_text: {
              type: 'string',
              description: 'The exact text to find and replace (must be unique in the HTML). Include enough context to make it unique.'
            },
            new_text: {
              type: 'string',
              description: 'The text to replace it with'
            },
            summary: {
              type: 'string',
              description: 'A brief summary of what change was made'
            }
          },
          required: ['old_text', 'new_text', 'summary']
        }
      }
    ]

    let fullText = ''
    const toolCalls: Array<{ id: string; name: string; input: unknown }> = []
    let currentToolUse: { id: string; name: string; input: string } | null = null

    try {
      const response = await fetch(`${PROXY_URL}/api/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          messages,
          system: SYSTEM_PROMPT,
          tools
        })
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to send message')
      }

      // Process SSE stream
      const reader = response.body?.getReader()
      const decoder = new TextDecoder()

      if (!reader) {
        throw new Error('No response body')
      }

      while (true) {
        const { done, value } = await reader.read()

        if (done) {
          break
        }

        const chunk = decoder.decode(value)
        const lines = chunk.split('\n\n')

        for (const line of lines) {
          if (!line.trim() || !line.startsWith('data: ')) {
            continue
          }

          const data = line.slice(6).trim()

          if (data === '[DONE]') {
            break
          }

          try {
            const event = JSON.parse(data)

            if (event.type === 'error') {
              throw new Error(event.error)
            }

            if (event.type === 'content_block_start') {
              if (event.content_block?.type === 'tool_use') {
                currentToolUse = {
                  id: event.content_block.id,
                  name: event.content_block.name,
                  input: ''
                }
              }
            } else if (event.type === 'content_block_delta') {
              if (event.delta?.type === 'text_delta') {
                fullText += event.delta.text
                this.config.onChunk?.(event.delta.text)
              } else if (event.delta?.type === 'input_json_delta' && currentToolUse) {
                currentToolUse.input += event.delta.partial_json
              }
            } else if (event.type === 'content_block_stop') {
              if (currentToolUse) {
                try {
                  const parsedInput = JSON.parse(currentToolUse.input)
                  toolCalls.push({
                    id: currentToolUse.id,
                    name: currentToolUse.name,
                    input: parsedInput
                  })
                  this.config.onToolCall?.(currentToolUse.name, parsedInput)
                } catch (e) {
                  console.error('Failed to parse tool input:', e)
                }
                currentToolUse = null
              }
            }
          } catch (e) {
            console.error('Failed to parse SSE event:', e)
          }
        }
      }

      this.config.onComplete?.(fullText)

      const message: ChatMessage = {
        id: nanoid(),
        role: 'assistant',
        content: fullText,
        timestamp: Date.now()
      }

      if (toolCalls.length > 0) {
        message.toolCalls = toolCalls
      }

      return message
    } catch (error) {
      this.config.onError?.(error as Error)
      throw error
    }
  }

}
